import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "./LanguageSelector.css";

const languages = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "zh-CN", name: "Chinese (Simplified)", nativeName: "中文 (简体)" },
  { code: "zh-TW", name: "Chinese (Traditional)", nativeName: "中文 (繁體)" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
] as const;

const getLanguageCode = (languageCode: string): string => {
  const codes: Record<string, string> = {
    en: "EN",
    "zh-CN": "简",
    "zh-TW": "繁",
    ja: "JA",
    ko: "KO",
  };
  return codes[languageCode] ?? "EN";
};

function matchLanguageIndex(language: string): number {
  const idx = languages.findIndex(
    (l) => language === l.code || language.startsWith(`${l.code}-`)
  );
  return idx >= 0 ? idx : 0;
}

const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  const currentIndex = matchLanguageIndex(i18n.language);
  const currentLanguage = languages[currentIndex] ?? languages[0];

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    setHighlightedIndex(matchLanguageIndex(i18n.language));
  }, [open, i18n.language]);

  useLayoutEffect(() => {
    if (!open) return;
    listboxRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const selectLanguage = useCallback(
    (code: string) => {
      void i18n.changeLanguage(code);
      setOpen(false);
      triggerRef.current?.focus();
    },
    [i18n]
  );

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      }
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => (i + 1) % languages.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => (i - 1 + languages.length) % languages.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      setHighlightedIndex(0);
    } else if (e.key === "End") {
      e.preventDefault();
      setHighlightedIndex(languages.length - 1);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      selectLanguage(languages[highlightedIndex].code);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`language-selector${open ? " language-selector--open" : ""}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="language-current"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={t("language.select")}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="language-code" aria-hidden>
          {getLanguageCode(currentLanguage.code)}
        </span>
        <span className="language-name">{currentLanguage.nativeName}</span>
        <span className="language-arrow" aria-hidden>
          ▼
        </span>
      </button>

      <div
        ref={listboxRef}
        id={listboxId}
        className="language-dropdown"
        role="listbox"
        aria-label={t("language.select")}
        aria-hidden={!open}
        tabIndex={open ? 0 : -1}
        onKeyDown={open ? onListKeyDown : undefined}
      >
        {languages.map((language, index) => {
          const selected = matchLanguageIndex(i18n.language) === index;
          const highlighted = open && index === highlightedIndex;
          return (
            <button
              key={language.code}
              id={`lang-option-${language.code}`}
              type="button"
              role="option"
              aria-selected={selected}
              tabIndex={-1}
              className={`language-option${selected ? " active" : ""}${highlighted ? " language-option--highlighted" : ""}`}
              onClick={() => selectLanguage(language.code)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span className="language-code" aria-hidden>
                {getLanguageCode(language.code)}
              </span>
              <span className="language-name">{language.nativeName}</span>
              <span className="language-english">{language.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageSelector;
